import { useQuery, useQueryClient } from '@tanstack/react-query'
import { gmailService } from '../services/gmailService'
import type { EmailFilters } from '../types'

export function useEmailList(filters: EmailFilters & { page?: number; limit?: number } = {}) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['emails', filters],
    queryFn: () => gmailService.listThreads(filters),
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const refresh = async () => {
    console.log('🔄 Refreshing email list...')
    // Clear cache completely
    queryClient.removeQueries({ queryKey: ['emails'] })
    // Force refetch
    await query.refetch()
    console.log('✅ Email list refreshed')
  }

  const markAsRead = async (threadId: string) => {
    await gmailService.markAsRead(threadId, true)
    await refresh()
  }

  const markAsUnread = async (threadId: string) => {
    await gmailService.markAsRead(threadId, false)
    await refresh()
  }

  const pinThread = async (threadId: string, isPinned: boolean) => {
    console.log('📌 Pinning thread:', threadId, 'isPinned:', isPinned)
    await gmailService.pinThread(threadId, isPinned)
    console.log('✅ Pin API call completed, refreshing...')
    await refresh()
    console.log('✅ Refresh completed')
  }

  const deleteThread = async (threadId: string) => {
    await gmailService.deleteThread(threadId)
    await refresh()
  }

  return {
    threads: query.data?.data || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    error: query.error,
    refresh,
    markAsRead,
    markAsUnread,
    pinThread,
    deleteThread,
  }
}

